
import { CommunityContract } from 'types/contracts';

import { CommunityContractSelectionType } from './types';
import { COMMUNITY_CONTRACT_TYPE } from '../../../../constants';

export const createDefaultCommunityContractSelection = (): CommunityContractSelectionType => ({
  overrides: {},
});

export const areCommunityContractSelectionsEqual = (
  left?: CommunityContractSelectionType | null,
  right?: CommunityContractSelectionType | null
) => {
  const leftOverrides = left?.overrides ?? {};
  const rightOverrides = right?.overrides ?? {};

  const leftKeys = Object.keys(leftOverrides);
  const rightKeys = Object.keys(rightOverrides);

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((key) => leftOverrides[Number(key)] === rightOverrides[Number(key)]);
};

export const getDefaultContractForCommunity = (contracts?: CommunityContract[]): CommunityContract | undefined => {
  if (!contracts || contracts.length === 0) {
    return undefined;
  }

  const rental = contracts.find((contract) => contract.type === COMMUNITY_CONTRACT_TYPE.RENTAL);
  if (rental) {
    return rental;
  }

  return contracts[0];
};

type ResolveOptions = {
  community?: { id: number; contracts?: CommunityContract[] } | null;
  competitors?: Array<{ id: number; contracts?: CommunityContract[] } & Record<string, any>> | null;
  selection?: CommunityContractSelectionType | null;
};

/**
 * Resolve the effective CommunityContract.id per community (user's own + each competitor).
 * Falls back to the default (Rental if available, otherwise first contract) when the user has
 * not made an explicit pick for a given community.
 */
export const createContractsByCommunityMap = ({ community, competitors, selection }: ResolveOptions) => {
  const overrides = selection?.overrides ?? {};
  const map: Record<number, number> = {};

  if (community?.id) {
    const fromOverride = overrides[community.id];
    if (fromOverride) {
      map[community.id] = fromOverride;
    } else {
      const fallback = getDefaultContractForCommunity(community.contracts);
      if (fallback?.id) {
        map[community.id] = fallback.id;
      }
    }
  }

  (competitors ?? []).forEach((competitor) => {
    if (!competitor?.id) {
      return;
    }
    const fromOverride = overrides[competitor.id];
    if (fromOverride) {
      map[competitor.id] = fromOverride;
      return;
    }
    const fallback = getDefaultContractForCommunity(competitor.contracts);
    if (fallback?.id) {
      map[competitor.id] = fallback.id;
    }
  });

  return map;
};
